import React, { useMemo } from 'react';
import { FormikProps } from 'formik';
import { useDidUpdate } from 'rooks';
import { Col, Row } from 'antd';
import { Input } from 'formik-antd';

import { useAppSelector } from 'hooks/useAppSelector';
import {
  countryCitiesSelector,
  isCountriesLoadingSelector,
  isCountryCitiesLoadingSelector,
  countriesSelector,
  isStatesLoadingSelector,
  statesSelector,
  isCitiesLoadingSelector,
  citiesSelector,
} from 'redux/selectors/regionSelectors';
import { MainLocation, WorksiteLocationFormValues } from '../WorksiteLocation';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import FormItem from 'components/formik/FormItem';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';
import Checkbox from 'components/formik/Checkbox';
import { getWorkLocationValue } from '../utils';
import { checkIsCountriesWithStatesDropdown } from 'utils/validators/countryUtils';

type Props = {
  formik: FormikProps<WorksiteLocationFormValues>;
  mainLocation: MainLocation;
  disabled?: boolean;
  isZipRequired?: boolean;
  mainLocationLabel: string;
};

export default function AssignmentWorkLocationForm(props: Props) {
  const {
    formik,
    mainLocation,
    disabled: isFormDisabled = false,
    isZipRequired = true,
    mainLocationLabel,
  } = props;
  const countries = useAppSelector(countriesSelector);
  const isCountriesLoading = useAppSelector(isCountriesLoadingSelector);
  const states = useAppSelector(statesSelector);
  const isStatesLoading = useAppSelector(isStatesLoadingSelector);
  const cities = useAppSelector(citiesSelector);
  const isCitiesLoading = useAppSelector(isCitiesLoadingSelector);
  const countryCities = useAppSelector(countryCitiesSelector);
  const isCountryCitiesLoading = useAppSelector(isCountryCitiesLoadingSelector);
  const isInternationalCountry = !checkIsCountriesWithStatesDropdown(
    getWorkLocationValue(formik, mainLocation, 'country'),
  );

  useDidUpdate(() => {
    if (formik.values.work.locationSameAsMain) {
      formik.validateField('work.zip');
    }
  }, [mainLocation.zip]);

  const filteredStates = useMemo(
    () =>
      states.filter(
        (state) =>
          state.countryId ===
          getWorkLocationValue(formik, mainLocation, 'country'),
      ),
    [formik, states, mainLocation],
  );

  const filteredCities = useMemo(
    () =>
      cities.filter(
        (city) =>
          city.stateId === getWorkLocationValue(formik, mainLocation, 'state'),
      ),
    [cities, formik, mainLocation],
  );

  const filteredInternationalCities = useMemo(
    () =>
      countryCities.filter((city) => {
        const country = formik.values.work.locationSameAsMain
          ? mainLocation.country
          : formik.values.work.country;
        return city.countryId === country;
      }),
    [formik.values.work, countryCities, mainLocation.country],
  );

  const handleCountryChange = (
    countryId: number,
    formik: FormikProps<WorksiteLocationFormValues>,
  ) => {
    formik.setFieldValue('work.city', '', false);
    formik.setFieldValue('work.state', undefined, false);
    formik.setFieldValue('work.internationalCityName', '', false);
    formik.setFieldValue('work.internationalStateName', '', false);
  };

  const handleStateChange = (
    formik: FormikProps<WorksiteLocationFormValues>,
  ) => {
    if (checkIsCountriesWithStatesDropdown(formik.values.work.country)) {
      formik.setFieldValue('work.city', '', false);
    } else {
      formik.setFieldValue('work.internationalCityName', undefined, false);
    }
  };

  const handleLocationSameAsMainChange = (
    e: any,
    formik: FormikProps<WorksiteLocationFormValues>,
  ) => {
    if (e.target.checked) {
      setTimeout(() => {
        formik.validateForm();
      }, 0);
      return;
    }

    formik.setFieldValue('work.country', undefined, false);
    formik.setFieldValue('work.state', undefined, false);
    formik.setFieldValue('work.internationalStateName', '', false);
    formik.setFieldValue('work.city', '', false);
    formik.setFieldValue('work.internationalCityName', '', false);
    formik.setFieldValue('work.address', '', false);
    formik.setFieldValue('work.zip', '', false);

    formik.setFieldTouched('work.country', true, false);
    formik.setFieldTouched('work.state', true, false);
    formik.setFieldTouched('work.internationalStateName', true, false);
    formik.setFieldTouched('work.city', true, false);
    formik.setFieldTouched('work.internationalCityName', true, false);
    formik.setFieldTouched('work.address', true, false);
    formik.setFieldTouched('work.zip', true, false);

    setTimeout(() => {
      formik.validateForm();
    }, 0);
  };

  return (
    <Col xs={24} md={12}>
      <div className="modal-title">Work location</div>

      <FormItem name="work.locationSameAsMain" className="checkbox-wrapper">
        <Checkbox
          name="work.locationSameAsMain"
          onChange={(e) => handleLocationSameAsMainChange(e, formik)}
          disabled={isFormDisabled}
        >
          {mainLocationLabel}
        </Checkbox>
      </FormItem>

      <Row gutter={20}>
        <Col xs={24} lg={12}>
          <FormItem name="work.country" label="Country *">
            <FormikSelectWithLoading
              isLoading={isCountriesLoading}
              name="work.country"
              showSearch
              placeholder="Select work office country"
              optionFilterProp="children"
              onChange={(value: number) => handleCountryChange(value, formik)}
              value={getWorkLocationValue(formik, mainLocation, 'country')}
              disabled={isFormDisabled || formik.values.work.locationSameAsMain}
            >
              {countries.map((country) => (
                <FormikSelectWithLoading.Option
                  key={`select-country-state-${country.id}`}
                  value={country.id || -1}
                >
                  {country.name}
                </FormikSelectWithLoading.Option>
              ))}
            </FormikSelectWithLoading>
          </FormItem>
        </Col>

        {!isInternationalCountry && (
          <Col xs={24} lg={12}>
            <FormItem name="work.state" label="State *">
              <FormikSelectWithLoading
                isLoading={isStatesLoading}
                name="work.state"
                showSearch
                placeholder="Select work office state"
                optionFilterProp="children"
                onChange={() => handleStateChange(formik)}
                value={getWorkLocationValue(formik, mainLocation, 'state')}
                disabled={
                  isFormDisabled ||
                  !formik.values.work.country ||
                  formik.values.work.locationSameAsMain
                }
              >
                {filteredStates.map((state) => (
                  <FormikSelectWithLoading.Option
                    key={`select-work-state-${state.id}`}
                    value={state.id || -1}
                  >
                    {state.name}
                  </FormikSelectWithLoading.Option>
                ))}
              </FormikSelectWithLoading>
            </FormItem>
          </Col>
        )}

        {isInternationalCountry && (
          <Col xs={24} lg={12}>
            <FormItem name="work.internationalStateName" label="State *">
              <Input
                name="work.internationalStateName"
                placeholder="Enter work office state"
                value={getWorkLocationValue(
                  formik,
                  mainLocation,
                  'internationalStateName',
                )}
                disabled={
                  isFormDisabled ||
                  !formik.values.work.country ||
                  formik.values.work.locationSameAsMain
                }
                autoComplete="off"
              />
            </FormItem>
          </Col>
        )}

        {isInternationalCountry && (
          <Col xs={24} lg={12}>
            <FormItem name="work.internationalCityName" label="City *">
              <FormikAutoCompleteWithLoading
                isLoading={isCountryCitiesLoading}
                name="work.internationalCityName"
                filterOption
                value={
                  getWorkLocationValue(
                    formik,
                    mainLocation,
                    'internationalCityName',
                  ) as string
                }
                disabled={
                  isFormDisabled ||
                  formik.values.work.locationSameAsMain ||
                  !formik.values.work.internationalStateName
                }
                showSearch
                placeholder="Enter work office city"
              >
                {filteredInternationalCities.map((city) => (
                  <FormikAutoCompleteWithLoading.Option
                    key={`work-city-${city.id}`}
                    value={city.name || ''}
                  >
                    {city.name}
                  </FormikAutoCompleteWithLoading.Option>
                ))}
              </FormikAutoCompleteWithLoading>
            </FormItem>
          </Col>
        )}

        {!isInternationalCountry && (
          <Col xs={24} lg={12}>
            <FormItem name="work.city" label="City *">
              <FormikAutoCompleteWithLoading
                isLoading={isCitiesLoading}
                name="work.city"
                filterOption
                placeholder="Select work office city"
                showSearch
                value={
                  getWorkLocationValue(formik, mainLocation, 'city') as string
                }
                disabled={
                  isFormDisabled ||
                  formik.values.work.locationSameAsMain ||
                  !formik.values.work.state
                }
              >
                {filteredCities.map((city) => (
                  <FormikAutoCompleteWithLoading.Option
                    key={`work-city-${city.id}`}
                    value={city.name || ''}
                  >
                    {city.name}
                  </FormikAutoCompleteWithLoading.Option>
                ))}
              </FormikAutoCompleteWithLoading>
            </FormItem>
          </Col>
        )}

        <Col xs={24} lg={12}>
          <FormItem label="Address *" name="work.address">
            <Input
              name="work.address"
              placeholder="Enter work address"
              value={
                getWorkLocationValue(formik, mainLocation, 'address') as string
              }
              disabled={isFormDisabled || formik.values.work.locationSameAsMain}
              autoComplete="off"
            />
          </FormItem>
        </Col>

        <Col xs={24} lg={12}>
          <FormItem
            label={isZipRequired ? 'ZIP code *' : 'ZIP code'}
            name="work.zip"
          >
            <Input
              name="work.zip"
              className="full-width"
              placeholder="Enter ZIP code"
              value={
                getWorkLocationValue(formik, mainLocation, 'zip') as string
              }
              disabled={isFormDisabled || formik.values.work.locationSameAsMain}
              autoComplete="off"
            />
          </FormItem>
        </Col>
      </Row>
    </Col>
  );
}
